import React, { useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { toastService } from '@services/toast.service.ts';
import { i18nGapsService, I18nGapRecord } from '@services/i18n-gaps.service.ts';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('uk-UA', { timeZone: userTimeZone });

export const DevI18nGapsPage: React.FC = () => {
  const [items, setItems] = useState<I18nGapRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    setIsLoading(true);
    try {
      const { items } = await i18nGapsService.list();
      setItems(items);
    } catch {
      toastService.error('Не вдалося завантажити список прогалин перекладу.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ключі інтерфейсу (`t('...')`), для яких i18next не знайшов переклад під час роботи застосунку.
          Збирається в пам'яті сервера (не в БД) — список зникає при перезапуску бекенду. Та сама подія
          паралельно пишеться в Betterstack-лог з тегом `I18nGaps`.
        </Typography>
        <PageCard>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Tooltip title="Оновити">
              <IconButton onClick={load} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Поки що нічого не зафіксовано.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Мова</TableCell>
                    <TableCell>Namespace</TableCell>
                    <TableCell>Ключ</TableCell>
                    <TableCell>Скільки разів</TableCell>
                    <TableCell>Вперше</TableCell>
                    <TableCell>Останній раз</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={`${item.language}-${item.ns}-${item.key}`} hover>
                      <TableCell><Chip label={item.language.toUpperCase()} size="small" variant="outlined" /></TableCell>
                      <TableCell>{item.ns}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.key}</TableCell>
                      <TableCell>{item.hitCount}</TableCell>
                      <TableCell>{formatDate(item.firstSeenAt)}</TableCell>
                      <TableCell>{formatDate(item.lastSeenAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </PageCard>
      </Box>
    </DashboardLayout>
  );
};
